import { useState } from 'react'

import UnsavedChangesModal from '../components/UnsavedChangesModal'
import { useFormContext } from '../utils/FormContext'

export interface LeaveGuardOptions {
  isDirty: boolean
  save: () => Promise<boolean>
  discard: () => void
  saving?: boolean
}

export const useLeaveGuard = ({
  isDirty,
  save,
  discard,
  saving = false,
}: LeaveGuardOptions) => {
  const { goBack } = useFormContext()
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null)

  const guard = (leave: () => void) => () => {
    if (isDirty) {
      setPendingLeave(() => leave)
    } else {
      leave()
    }
  }

  const leaveGuardModal = (
    <UnsavedChangesModal
      opened={pendingLeave !== null}
      saving={saving}
      onSaveAndLeave={async () => {
        const leave = pendingLeave
        let saved = false
        try {
          saved = await save()
        } finally {
          setPendingLeave(null)
        }
        if (saved) leave?.()
      }}
      onDiscard={() => {
        const leave = pendingLeave
        discard()
        setPendingLeave(null)
        leave?.()
      }}
      onKeepEditing={() => setPendingLeave(null)}
    />
  )

  return {
    goBack: guard(goBack),
    leaveGuardModal,
  }
}

export const useFormLeaveGuard = (buildPayload: (values) => object) => {
  const {
    form,
    saveSection,
    saveProgressLoading,
    discardChanges,
    hasUnsavedChanges,
  } = useFormContext()

  return useLeaveGuard({
    isDirty: hasUnsavedChanges,
    save: () => saveSection(buildPayload(form.values)),
    discard: discardChanges,
    saving: saveProgressLoading,
  })
}
